/**
 * WorkflowStepReview (Step 7)
 *
 * Final review and submission readiness.
 * Substeps: summary, checklist, warnings, packetPreview
 *
 * Part of Phase 8: Job Workflow Wizard
 */

import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import {
  FileText,
  Check,
  CheckCircle,
  AlertTriangle,
  XCircle,
  Download,
  ExternalLink,
  Package,
  MapPin,
  Wrench,
  DollarSign,
  User,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import type { UseJobWorkflowReturn } from '@/hooks/useJobWorkflow';
import type { JobData } from '../WorkflowContainer';

interface WorkflowStepReviewProps {
  jobId: string;
  contractorId: string;
  jobData: JobData;
  workflow: UseJobWorkflowReturn;
}

export function WorkflowStepReview({
  jobId,
  contractorId,
  jobData,
  workflow,
}: WorkflowStepReviewProps) {
  const [acknowledgedWarnings, setAcknowledgedWarnings] = useState(false);

  const progress = workflow.progress;
  const hasBlockers = workflow.hasBlockers;
  const isReady = workflow.isReadyForSubmission;
  const issues = workflow.getIssuesForCurrentStep();

  // Mock packet documents
  const packetDocuments = [
    { name: 'Permit Application Form', status: 'ready' },
    { name: 'Contractor License Copy', status: 'ready' },
    { name: 'Proof of Insurance', status: 'ready' },
    { name: 'Plans/Drawings', status: 'missing' },
    { name: 'Load Calculation', status: 'missing' },
  ];

  const readyDocs = packetDocuments.filter((d) => d.status === 'ready').length;
  const totalDocs = packetDocuments.length;

  return (
    <div className="space-y-6">
      {/* Ready Status Banner */}
      <Card
        className={cn(
          'border-2',
          isReady
            ? 'border-green-500 bg-green-50/50 dark:border-green-800 dark:bg-green-950/30'
            : 'border-amber-500 bg-amber-50/50 dark:border-amber-800 dark:bg-amber-950/30'
        )}
      >
        <CardContent className="py-6">
          <div className="flex items-center gap-4">
            {isReady ? (
              <CheckCircle className="h-12 w-12 text-green-500" />
            ) : hasBlockers ? (
              <XCircle className="h-12 w-12 text-red-500" />
            ) : (
              <AlertTriangle className="h-12 w-12 text-amber-500" />
            )}
            <div>
              <h3
                className={cn(
                  'text-xl font-semibold',
                  isReady
                    ? 'text-green-700 dark:text-green-300'
                    : hasBlockers
                    ? 'text-red-700 dark:text-red-300'
                    : 'text-amber-700 dark:text-amber-300'
                )}
              >
                {isReady
                  ? 'Ready for Submission!'
                  : hasBlockers
                  ? 'Blockers Must Be Resolved'
                  : 'Almost Ready'}
              </h3>
              <p
                className={cn(
                  'text-sm mt-1',
                  isReady
                    ? 'text-green-600 dark:text-green-300'
                    : hasBlockers
                    ? 'text-red-600 dark:text-red-300'
                    : 'text-amber-600 dark:text-amber-300'
                )}
              >
                {isReady
                  ? 'All requirements met. You can proceed with permit submission.'
                  : hasBlockers
                  ? `${issues.blockers.length} blocking issues need to be fixed.`
                  : `${issues.warnings.length} warnings to review before submission.`}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Job Summary */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Job Summary
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="flex items-start gap-3">
              <MapPin className="h-5 w-5 text-muted-foreground mt-0.5" />
              <div>
                <p className="text-sm text-muted-foreground">Address</p>
                <p className="font-medium">{jobData.location || 'Not specified'}</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <MapPin className="h-5 w-5 text-muted-foreground mt-0.5" />
              <div>
                <p className="text-sm text-muted-foreground">Jurisdiction</p>
                <p className="font-medium">{jobData.jurisdictionAhjName || 'Not resolved'}</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Wrench className="h-5 w-5 text-muted-foreground mt-0.5" />
              <div>
                <p className="text-sm text-muted-foreground">Permit Type</p>
                <p className="font-medium capitalize">
                  {jobData.trade || jobData.permitType || 'Not selected'}
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <DollarSign className="h-5 w-5 text-muted-foreground mt-0.5" />
              <div>
                <p className="text-sm text-muted-foreground">Project Value</p>
                <p className="font-medium">
                  {jobData.estimatedValue
                    ? `$${jobData.estimatedValue.toLocaleString()}`
                    : 'Not specified'}
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3 sm:col-span-2">
              <User className="h-5 w-5 text-muted-foreground mt-0.5" />
              <div>
                <p className="text-sm text-muted-foreground">Scope</p>
                <p className="font-medium">{jobData.jobScope || 'Not specified'}</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Completion Checklist */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Check className="h-5 w-5" />
            Completion Checklist
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div
              className={cn(
                'flex items-center justify-between p-3 rounded-lg',
                progress.dataCompletenessPct === 100
                  ? 'bg-green-50 dark:bg-green-950/30'
                  : 'bg-amber-50 dark:bg-amber-950/30'
              )}
            >
              <div className="flex items-center gap-2">
                {progress.dataCompletenessPct === 100 ? (
                  <CheckCircle className="h-5 w-5 text-green-500" />
                ) : (
                  <AlertTriangle className="h-5 w-5 text-amber-500" />
                )}
                <span>Required fields complete</span>
              </div>
              <Badge
                variant={progress.dataCompletenessPct === 100 ? 'default' : 'secondary'}
              >
                {progress.dataCompletenessPct}%
              </Badge>
            </div>

            <div
              className={cn(
                'flex items-center justify-between p-3 rounded-lg',
                progress.packetCompletenessPct === 100
                  ? 'bg-green-50 dark:bg-green-950/30'
                  : 'bg-amber-50 dark:bg-amber-950/30'
              )}
            >
              <div className="flex items-center gap-2">
                {progress.packetCompletenessPct === 100 ? (
                  <CheckCircle className="h-5 w-5 text-green-500" />
                ) : (
                  <AlertTriangle className="h-5 w-5 text-amber-500" />
                )}
                <span>Required documents uploaded</span>
              </div>
              <Badge
                variant={progress.packetCompletenessPct === 100 ? 'default' : 'secondary'}
              >
                {progress.packetCompletenessPct}%
              </Badge>
            </div>

            <div
              className={cn(
                'flex items-center justify-between p-3 rounded-lg',
                !hasBlockers
                  ? 'bg-green-50 dark:bg-green-950/30'
                  : 'bg-red-50 dark:bg-red-950/30'
              )}
            >
              <div className="flex items-center gap-2">
                {!hasBlockers ? (
                  <CheckCircle className="h-5 w-5 text-green-500" />
                ) : (
                  <XCircle className="h-5 w-5 text-red-500" />
                )}
                <span>No blocking issues</span>
              </div>
              <Badge variant={!hasBlockers ? 'default' : 'destructive'}>
                {hasBlockers ? `${issues.blockers.length} blockers` : 'Passed'}
              </Badge>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Warnings Acknowledgment */}
      {issues.warnings.length > 0 && (
        <Card className="border-amber-200 dark:border-amber-800">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2 text-amber-700 dark:text-amber-300">
              <AlertTriangle className="h-5 w-5" />
              Warnings to Review
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {issues.warnings.map((warning) => (
                <div
                  key={warning.id}
                  className="p-3 rounded-lg bg-amber-50 border border-amber-200 dark:bg-amber-950/30 dark:border-amber-800"
                >
                  <p className="text-sm text-amber-700 dark:text-amber-300">{warning.message}</p>
                </div>
              ))}

              <Separator />

              <div className="flex items-start gap-3 pt-2">
                <Checkbox
                  id="ackWarnings"
                  checked={acknowledgedWarnings}
                  onCheckedChange={(checked) => setAcknowledgedWarnings(checked === true)}
                  className="mt-0.5"
                />
                <div>
                  <Label htmlFor="ackWarnings" className="font-medium cursor-pointer">
                    I have reviewed these warnings and wish to proceed
                  </Label>
                  <p className="text-sm text-muted-foreground mt-1">
                    These issues may cause delays but won't prevent submission.
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Document Packet Preview */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center justify-between">
            <span className="flex items-center gap-2">
              <Package className="h-5 w-5" />
              Submission Packet
            </span>
            <Badge variant="outline">
              {readyDocs} / {totalDocs} Ready
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {packetDocuments.map((doc, idx) => (
              <div
                key={idx}
                className={cn(
                  'flex items-center justify-between p-3 rounded-lg border',
                  doc.status === 'ready' && 'bg-green-50/50 border-green-200 dark:bg-green-950/30 dark:border-green-800',
                  doc.status === 'missing' && 'bg-red-50/50 border-red-200 dark:bg-red-950/30 dark:border-red-800'
                )}
              >
                <div className="flex items-center gap-2">
                  <FileText
                    className={cn(
                      'h-4 w-4',
                      doc.status === 'ready' ? 'text-green-500' : 'text-red-500'
                    )}
                  />
                  <span className="text-sm font-medium">{doc.name}</span>
                </div>
                {doc.status === 'ready' ? (
                  <CheckCircle className="h-4 w-4 text-green-500" />
                ) : (
                  <Badge variant="destructive" className="text-xs">
                    Missing
                  </Badge>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Action Buttons */}
      <Card>
        <CardContent className="py-6">
          <div className="grid gap-3 sm:grid-cols-3">
            <Button variant="outline" className="w-full">
              <Download className="h-4 w-4 mr-2" />
              Export Packet (ZIP)
            </Button>
            <Button variant="outline" className="w-full">
              <FileText className="h-4 w-4 mr-2" />
              Generate Application PDF
            </Button>
            <Button variant="outline" className="w-full">
              <ExternalLink className="h-4 w-4 mr-2" />
              View Portal Guidance
            </Button>
          </div>

          <Separator className="my-6" />

          <div className="text-center">
            <Button
              size="lg"
              disabled={!isReady || (issues.warnings.length > 0 && !acknowledgedWarnings)}
              className={cn(
                'min-w-[200px]',
                isReady && 'bg-green-600 hover:bg-green-700'
              )}
            >
              <Check className="h-5 w-5 mr-2" />
              Mark as Submitted
            </Button>
            <p className="text-sm text-muted-foreground mt-2">
              After submitting, enter your confirmation number to track status.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
